import { useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DeskTable, InlineDateTime, muted, type Column } from '@/components/common/DeskTable'
import StatusBadge from '@/components/common/StatusBadge'
import FilterBarApply from '@/components/common/FilterBarApply'
import FilterBarForm from '@/components/common/FilterBarForm'
import PageFrame from '@/components/common/PageFrame'
import { Button } from '@/components/ui/button'
import { Sheet } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import FilterSearch from '@/components/common/FilterSearch'
import { GlassSelect } from '@/components/ui/glass-select'
import { backofficeApi, type LiveAccount } from '@/lib/api-client'
import { ACCOUNT_WIDTH, accountColumnsForWidth } from '@/lib/column-widths'
import { dash, fmtDateTime, splitIb } from '@/lib/format'
import { accountTypeLabel, bookLabel } from '@/lib/labels'
import { useDeskSort } from '@/hooks/useDeskSort'
import { useListPageSize } from '@/hooks/useListPageSize'
import { FILTER_BAR } from '@/lib/ui-shell'

function ibTeam(raw?: string | null) {
  const { ib, team } = splitIb(raw)
  return ib === '—' && team === '—' ? '—' : `${ib} / ${team}`
}

const clip = 'overflow-hidden'

/** MT5 groups all start with the same server path; the last two segments are what tells rows apart. */
function groupTail(group?: string | null): string {
  return group ? group.split('\\').slice(-2).join('\\') : '—'
}

const accountColumns: Column<LiveAccount>[] = [
  { key: 'login', sortKey: 'login', header: 'Login', sticky: true, width: ACCOUNT_WIDTH.login, mobile: 'title', cell: (r) => <span className="font-mono">{r.login_id}</span> },
  { key: 'code', sortKey: 'code', header: 'Client code', width: ACCOUNT_WIDTH.code, mobile: 'subtitle', cell: (r) => <span className="font-mono">{muted(dash(r.client_code))}</span> },
  { key: 'type', sortKey: 'type', header: 'Type', width: ACCOUNT_WIDTH.type, mobile: 'field', cell: (r) => accountTypeLabel(r.account_type) },
  { key: 'group', sortKey: 'group', header: 'Group', width: ACCOUNT_WIDTH.group, className: clip, cell: (r) => <span className="block truncate" title={r.group || undefined}>{muted(groupTail(r.group))}</span> },
  { key: 'lev', sortKey: 'lev', header: 'Leverage', align: 'right', width: ACCOUNT_WIDTH.lev, cell: (r) => (r.leverage ? `1:${r.leverage}` : '—') },
  { key: 'status', sortKey: 'status', header: 'Status', width: ACCOUNT_WIDTH.status, className: 'whitespace-normal', mobile: 'status', cell: (r) => <StatusBadge className="whitespace-normal" status={r.status} /> },
  { key: 'sync', sortKey: 'sync', sortFirst: 'desc', header: 'Last sync (WIB)', width: ACCOUNT_WIDTH.sync, className: 'overflow-hidden', mobile: 'field', cell: (r) => <InlineDateTime value={r.last_sync_at} /> },
  { key: 'ib', header: 'IB / team', headerTitle: 'Introducing broker / team', width: ACCOUNT_WIDTH.ib, className: clip, cell: (r) => <span className="block truncate" title={ibTeam(r.ib_team)}>{muted(ibTeam(r.ib_team))}</span> },
]

export function LiveAccountsPage({
  title = 'Live Accounts',
  description = 'Real-money trading accounts and their MT5 login, group and sync status.',
  forceType,
  defaultType = '',
}: {
  title?: string
  description?: string
  forceType?: string
  /** Used when the URL has no account_type. "all" is an explicit choice. */
  defaultType?: '' | 'real'
}) {
  const [sp, setSp] = useSearchParams()
  const page = Number(sp.get('page') || '1')
  const pageSize = useListPageSize()
  const rawType = sp.get('account_type')
  const accountType = forceType || (rawType === 'all' ? '' : rawType ?? defaultType)
  const [draftQ, setDraftQ] = useState(sp.get('q') || '')
  const appliedQ = sp.get('q') || ''
  const [open, setOpen] = useState<LiveAccount | null>(null)
  const rowRef = useRef<HTMLElement | null>(null)
  const { sort, onSortChange, sortQuery } = useDeskSort(['login', 'code', 'type', 'group', 'lev', 'status', 'sync'])
  const q = useQuery({
    queryKey: ['live-accounts', accountType, appliedQ, page, pageSize, sortQuery],
    queryFn: () =>
      backofficeApi.listAccounts({
        q: appliedQ || undefined,
        account_type: accountType || undefined,
        page,
        page_size: pageSize,
        ...sortQuery,
      }),
  })
  const items = q.data?.items ?? []
  function patch(next: Record<string, string>) {
    const cur = new URLSearchParams(sp)
    for (const [k, v] of Object.entries(next)) {
      if (v) cur.set(k, v)
      else cur.delete(k)
    }
    if (!('page' in next)) cur.set('page', '1')
    setSp(cur, { replace: true })
  }
  return (
    <PageFrame title={title} description={description}>
      <FilterBarForm onSubmit={() => patch({ q: draftQ })} className={FILTER_BAR}>
        <div className="flex max-lg:flex-col max-lg:gap-3 lg:flex-row lg:flex-wrap lg:items-end lg:gap-3">
          <FilterSearch value={draftQ} onChange={setDraftQ} placeholder="Login or client code" className="min-w-48 flex-1" />
          {forceType ? null : (
            <Field label="Type" className="min-w-36">
              <GlassSelect
                value={accountType || 'all'}
                onChange={(next) => patch({ account_type: next })}
                options={[
                  { value: 'all', label: 'All types' },
                  { value: 'real', label: 'Live' },
                  { value: 'demo', label: 'Demo' },
                ]}
              />
            </Field>
          )}
          <FilterBarApply
            isDirty={draftQ !== appliedQ}
            canReset={Boolean(draftQ || appliedQ)}
            onReset={() => {
              setDraftQ('')
              patch({ q: '' })
            }}
          />
        </div>
      </FilterBarForm>
      <DeskTable
        columns={accountColumns}
        rows={items}
        rowKey={(r) => String(r.login_id)}
        selectedKey={open ? String(open.login_id) : null}
        onRowClick={(row, el) => {
          rowRef.current = el
          setOpen(row)
        }}
        loading={q.isLoading}
        error={q.error}
        emptyTitle="No accounts"
        emptyDescription="Nothing matches this search."
        sort={sort}
        onSortChange={onSortChange}
        visibleKeys={accountColumnsForWidth}
        paging={{
          total: q.data?.total ?? 0,
          page,
          pageSize,
          onPage: (p) => patch({ page: String(p) }),
          onPageSize: (size) => patch({ page_size: String(size), page: '1' }),
        }}
      />
      <Sheet open={Boolean(open)} onClose={() => setOpen(null)} label="Account detail" returnFocusRef={rowRef}>
        {open ? (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-mono text-lg font-semibold">{open.login_id}</h2>
              <Button variant="ghost" size="sm" onClick={() => setOpen(null)}>
                Close
              </Button>
            </div>
            <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1.5 text-sm">
              {(
                [
                  ['Client code', <span key="code" className="font-mono">{dash(open.client_code)}</span>],
                  ['Type', accountTypeLabel(open.account_type)],
                  ['Group', dash(open.group)],
                  ['Leverage', open.leverage ? `1:${open.leverage}` : '—'],
                  ['Status', <StatusBadge key="status" status={open.status} />],
                  ['Last sync', fmtDateTime(open.last_sync_at)],
                  ['IB / team', ibTeam(open.ib_team)],
                  ['Platform', bookLabel(open.platform)],
                  ['Book', bookLabel(open.book)],
                ] as [string, ReactNode][]
              ).map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="break-words">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
      </Sheet>
    </PageFrame>
  )
}
